import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { buildWhatsAppUrl, WHATSAPP_URL } from "@/components/WhatsAppButton";
import { buildTelegramUrl } from "@/components/TelegramButton";

export const useMessengerLinks = () => {
  const [wa, setWa] = useState(WHATSAPP_URL);
  const [tg, setTg] = useState("https://t.me/findrates_bot");

  useEffect(() => {
    setWa(buildWhatsAppUrl());
    setTg(buildTelegramUrl());
  }, []);

  return { wa, tg };
};

type ChromeProps = {
  wa: string;
  tg: string;
};

type HeaderProps = ChromeProps & {
  onDemo?: () => void;
  showDemo?: boolean;
  onChat: () => void;
  showHowItWorks?: boolean;
};

export const LandingHeader = ({
  wa,
  tg,
  onDemo,
  onChat,
  showHowItWorks = true,
  showDemo = true,
}: HeaderProps) => (
  <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur">
    <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4">
      <span className="text-sm font-semibold uppercase tracking-[0.2em]">findrates.ai</span>

      <nav className="flex items-center gap-0">
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden border border-border bg-card px-3 py-2 text-xs uppercase tracking-widest transition-colors hover:bg-secondary md:block"
        >
          WhatsApp
        </a>
        <a
          href={tg}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden border border-l-0 border-border bg-card px-3 py-2 text-xs uppercase tracking-widest transition-colors hover:bg-secondary md:block"
        >
          Telegram
        </a>
        {showHowItWorks && (
          <a
            href="#how-it-works"
            className="hidden border border-border bg-card px-4 py-2 text-xs uppercase tracking-widest transition-colors hover:bg-secondary sm:block md:border-l-0"
          >
            How it works
          </a>
        )}
        {showDemo && onDemo && (<button
          type="button"
          onClick={onDemo}
          className="hidden border border-accent-strong bg-accent-strong px-4 py-2 text-xs uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90 sm:block"
        >
          Free demo
        </button>)}
        <button
          type="button"
          onClick={onChat}
          className="border border-accent-strong bg-accent-strong px-4 py-2 text-xs uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
        >
          Get rates
        </button>
      </nav>
    </div>
  </header>
);

export const LandingFooter = ({ wa, tg }: ChromeProps) => (
  <footer className="border-t border-border">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-8 text-xs uppercase tracking-widest text-muted-foreground sm:flex-row">
      <span className="text-foreground">findrates.ai</span>
      <nav className="flex flex-wrap items-center justify-center gap-4">
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="transition-colors hover:text-foreground"
        >
          WhatsApp
        </a>
        <span aria-hidden="true">·</span>
        <a
          href={tg}
          target="_blank"
          rel="noopener noreferrer"
          className="transition-colors hover:text-foreground"
        >
          Telegram
        </a>
        <span aria-hidden="true">·</span>
        <Link to="/privacy" className="transition-colors hover:text-foreground">
          Privacy
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/bot/terms" className="transition-colors hover:text-foreground">
          Terms
        </Link>
        <span aria-hidden="true">·</span>
        <a href="mailto:info@findrates.ai" className="transition-colors hover:text-foreground">
          Contact
        </a>
      </nav>
    </div>
  </footer>
);

export const SectionTag = ({ num, label }: { num: string; label: string }) => (
  <div className="flex items-center text-xs uppercase tracking-widest">
    <span className="bg-accent-strong px-2 py-1 text-accent-foreground">{num}</span>
    <span className="border border-l-0 border-border bg-card px-3 py-1 text-foreground">{label}</span>
  </div>
);

export const Corner = () => (
  <span
    aria-hidden="true"
    className="absolute left-0 top-0 h-3 w-3 border-l-2 border-t-2"
    style={{ borderColor: "hsl(var(--accent))" }}
  />
);
